import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../../../firebase-client';
import { collection, doc, getDoc } from 'firebase/firestore';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import { buildMirrorCalendarEvents } from '../../../utils/calendarMirrorEvents';
import StandardCalendarView from '../../../components/calendar/StandardCalendarView';
import CalendarSourceSwitcher from '../../../components/calendar/CalendarSourceSwitcher';
import EventFormModal from '../../../components/forms/teamForms/eventFormModal';
import { useAuth } from '../../../auth/AuthContext';
import { buildEventModalStateFromMirrorEvent, isAdminTeamRole } from '../../../components/calendar/mirrorEventModal';
import { buildEventCopyInitialData } from '../../../components/forms/teamForms/events/eventCopy';
async function loadTeamAdminAccess(teamId, userId) {
  if (!teamId || !userId) return { isEventAdmin: false, canModerateEventComments: false, canManageEventRefunds: false };
  try {
    const [teamSnap, assignmentSnap] = await Promise.all([
      getDoc(doc(db, 'teams', teamId)),
      getDoc(doc(db, 'teams', teamId, 'adminAssignments', userId)).catch(() => null),
    ]);
    const teamData = teamSnap.exists() ? teamSnap.data() || {} : {};
    const fullAdmin = Array.isArray(teamData.admins) && teamData.admins.includes(userId);
    const assignedKeys = assignmentSnap?.exists() && Array.isArray(assignmentSnap.data()?.permissionKeys)
      ? assignmentSnap.data().permissionKeys
      : [];
    return {
      isEventAdmin: fullAdmin || assignedKeys.includes('events.manage'),
      canModerateEventComments: fullAdmin || assignedKeys.includes('communications.manage'),
      canManageEventRefunds: fullAdmin || assignedKeys.includes('finance.manage'),
    };
  } catch {
    return { isEventAdmin: false, canModerateEventComments: false, canManageEventRefunds: false };
  }
}
async function loadRsvpSeriesResponseSettings(rows) {
  const teamIds = Array.from(new Set(
    (rows || [])
      .map((row) => row?.data?.teamId)
      .filter((teamId) => typeof teamId === 'string' && teamId.trim() !== '')
  ));
  const entries = await Promise.all(teamIds.map(async (teamId) => {
    try {
      const snap = await getDoc(doc(db, 'teams', teamId));
      return [teamId, snap.exists() ? snap.data()?.eventSettings?.rsvpSeriesResponsesEnabled !== false : true];
    } catch {
      return [teamId, true];
    }
  }));
  return Object.fromEntries(entries);
}
export default function FamilyCalendarPage() {
  const {
    user
  } = useAuth();
  const {
    familyId
  } = useParams();
  const [events, setEvents] = useState([]);
  const [familyName, setFamilyName] = useState('');
  const [teamRoles, setTeamRoles] = useState({});
  const [adminAccessByTeam, setAdminAccessByTeam] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  useEffect(() => {
    if (!familyId) return;
    let cancelled = false;
    const ref = collection(db, `families/${familyId}/calendar`);
    const unsub = safeOnSnapshot(ref, async snap => {
      const rows = snap.docs.map(d => ({
        id: d.id,
        data: d.data() || {}
      }));
      const rsvpSeriesResponsesByTeam = await loadRsvpSeriesResponseSettings(rows);
      if (cancelled) return;
      setEvents(buildMirrorCalendarEvents(rows, {
        maxOccurrences: 24,
        seriesMode: 'full',
        rsvpSeriesResponsesByTeam
      }));
    }, err => void 0);
    (async () => {
      try {
        const famDoc = await getDoc(doc(db, 'families', familyId));
        setFamilyName(famDoc.exists() ? famDoc.data().name : 'Family');
      } catch (err) {}
    })();
    return () => {
      cancelled = true;
      unsub && unsub();
    };
  }, [familyId]);
  useEffect(() => {
    if (!user?.uid) {
      setTeamRoles({});
      return;
    }
    (async () => {
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        setTeamRoles(userSnap.exists() ? userSnap.data()?.team_roles || {} : {});
      } catch {
        setTeamRoles({});
      }
    })();
  }, [user?.uid]);
  useEffect(() => {
    if (!user?.uid || !selectedTeamId) return;
    let cancelled = false;
    loadTeamAdminAccess(selectedTeamId, user.uid).then((access) => {
      if (cancelled) return;
      setAdminAccessByTeam((current) => ({
        ...current,
        [selectedTeamId]: access,
      }));
    });
    return () => {
      cancelled = true;
    };
  }, [selectedTeamId, user?.uid]);
  const modalIsOpen = !!(selectedEvent && selectedTeamId);
  const selectedAdminAccess = selectedTeamId ? adminAccessByTeam[selectedTeamId] : null;
  const modalIsAdmin = isAdminTeamRole(teamRoles?.[selectedTeamId]) || Boolean(selectedAdminAccess?.isEventAdmin);
  const canModerateEventComments = Boolean(selectedAdminAccess?.canModerateEventComments || isAdminTeamRole(teamRoles?.[selectedTeamId]));
  const canManageEventRefunds = Boolean(selectedAdminAccess?.canManageEventRefunds || isAdminTeamRole(teamRoles?.[selectedTeamId]));
  return <>
      <StandardCalendarView title={`${familyName || 'Family'} Committed Calendar`} events={events} toolbarStart={<CalendarSourceSwitcher compact currentScope="family" currentFamilyId={familyId || ''} currentFamilyName={familyName || ''} />} selectable={false} onSelectEvent={event => {
      const next = buildEventModalStateFromMirrorEvent(event);
      if (!next.teamId || !next.initialData) return;
      setSelectedTeamId(next.teamId);
      setSelectedEvent(next.initialData);
    }} />

      {modalIsOpen && <EventFormModal key={selectedEvent._copyToken || selectedEvent.id || selectedEvent.originalId || 'new-event'} teamId={selectedTeamId} isOpen={true} onClose={() => {
      setSelectedEvent(null);
      setSelectedTeamId(null);
    }} onCopy={(event) => setSelectedEvent(buildEventCopyInitialData(event))} initialData={selectedEvent} readOnly={!modalIsAdmin} userId={user?.uid || ''} isAdmin={modalIsAdmin} canModerateEventComments={canModerateEventComments} canManageEventRefunds={canManageEventRefunds} teamBrand={{}} calendarSurface="family" />}
    </>;
}
